// Per-pane DOM overlays: source labels, state badges (P-13, F-04 "每格角落的狀態標示") and drop zones (F-01.1).

import type { App } from '../app/app';
import type { Pane } from '../app/view';
import type { SourceId } from '../core/types';
import { coverageInfo, transformsEqual } from '../core/alignment';
import { t, type Lang } from '../i18n';
import { $, esc, fmtSigned } from './dom';
import { slotLabel } from '../core/types';

function badge(text: string, cls = ''): string {
  return `<span class="badge ${cls}">${esc(text)}</span>`;
}

const pct2 = (v: number) => (Math.round(v * 10000) / 100).toFixed(2);
const r1 = (v: number) => Math.round(v * 10) / 10;

export function edgesText(e: { l: number; r: number; t: number; b: number }, lang?: Lang): string {
  return (['l', 'r', 't', 'b'] as const)
    .filter((k) => e[k] > 0.05)
    .map((k) => `${t('edge.' + k, undefined, lang)} ${r1(e[k])}`)
    .join(' ');
}

/** "1988×1088 · 拉伸到 A · 縮放 X 96.58% Y 99.26% · 裁切 …" (F-03.4). */
export function alignSummary(app: App, id: SourceId, lang?: Lang): string {
  const s = app.sources.source(id);
  const a = app.sources.source('A');
  if (!s || !a) return '';
  const tr = s.spatialTransform;
  const parts = [`${s.spec.width}×${s.spec.height}`, t('align.mode.' + tr.mode, undefined, lang)];
  if (Math.abs(tr.sx - 1) > 1e-6 || Math.abs(tr.sy - 1) > 1e-6) parts.push(t('align.scale', { sx: pct2(tr.sx), sy: pct2(tr.sy) }, lang));
  if (tr.mode === 'manual' && (Math.abs(tr.dx) > 0.05 || Math.abs(tr.dy) > 0.05)) parts.push(t('align.shift', { dx: r1(tr.dx), dy: r1(tr.dy) }, lang));
  const c = coverageInfo(tr, { w: a.spec.width, h: a.spec.height }, { w: s.spec.width, h: s.spec.height });
  const crop = edgesText({ l: c.cropLeft, r: c.cropRight, t: c.cropTop, b: c.cropBottom }, lang);
  const gap = edgesText({ l: c.blackLeft, r: c.blackRight, t: c.blackTop, b: c.blackBottom }, lang);
  if (crop) parts.push(t('align.crop', { v: crop }, lang));
  if (gap) parts.push(t('align.gap', { v: gap }, lang));
  return parts.join(' · ');
}

export function sourceLabel(app: App, id: SourceId): string {
  const s = app.sources.source(id);
  if (!s) return slotLabel(id);
  return `${slotLabel(id)} · ${s.file.name}`;
}

/**
 * Alignment reminder (F-03.10): what the suggestion would change for a version ("shift 12, -3 px · scale …"), or
 * null when there is nothing to point out (no suggestion, already applied, still checking, or dismissed).
 */
export function pendingAlignment(app: App, id: SourceId): string | null {
  const sg = app.sources.suggestions.get(id);
  const s = app.sources.source(id);
  if (!sg || !s || app.alignIgnored.has(id) || app.alignChecking.has(id) || app.autoAligning.has(id)) return null;
  const cur = s.spatialTransform;
  const tr = sg.transform;
  if (transformsEqual(tr, cur, 0.05)) return null;
  const parts: string[] = [];
  if (Math.abs(tr.dx - cur.dx) >= 0.5 || Math.abs(tr.dy - cur.dy) >= 0.5) parts.push(t('align.hintShift', { dx: fmtSigned(r1(tr.dx - cur.dx)), dy: fmtSigned(r1(tr.dy - cur.dy)) }));
  if (Math.abs(tr.sx - cur.sx) >= 0.001 || Math.abs(tr.sy - cur.sy) >= 0.001) parts.push(t('align.hintScale', { sx: pct2(tr.sx), sy: pct2(tr.sy) }));
  return parts.join(' · ') || t('align.mode.' + sg.scores[0].mode);
}

/** The versions with a pending alignment reminder (toolbar highlight, export warning). */
export function pendingAlignments(app: App): SourceId[] {
  return app.sources.loadedBs().filter((id) => pendingAlignment(app, id) !== null);
}

function sourceBadges(app: App, id: SourceId, out: string[], actions = false): void {
  const s = app.sources.source(id);
  if (!s) return;
  const at = app.sources.frameAt(id, app.player.tick);
  if (at && at.held) out.push(badge(t('badge.outOfRange', { id }), 'warn'));
  if (at?.noCounterpart && id === 'A') out.push(badge(t('badge.noCounterpart'), 'warn'));
  if (at?.approximate) out.push(badge(t('badge.approximate', { id }), 'warn'));
  const a = app.sources.source('A');
  if (id === 'A') {
    if (app.project.compareScale > 1) out.push(badge(t('badge.grid', { id, p: app.project.compareScale * 100 }), 'info'));
  } else if (a) {
    const tr = s.spatialTransform;
    const identity = Math.abs(tr.dx) < 1e-6 && Math.abs(tr.dy) < 1e-6 && Math.abs(tr.sx - 1) < 1e-6 && Math.abs(tr.sy - 1) < 1e-6;
    if (!identity || s.spec.width !== a.spec.width || s.spec.height !== a.spec.height) out.push(badge(`${slotLabel(id)} ${alignSummary(app, id)}`, 'info'));
    const what = pendingAlignment(app, id);
    if (what) {
      const text = t('badge.alignHint', { id: slotLabel(id), what });
      out.push(
        actions
          ? `<span class="badge warn align-hint"><span>${esc(text)}</span><button data-align-apply="${id}">${esc(t('align.hintApply'))}</button><button data-align-ignore="${id}">${esc(t('align.hintIgnore'))}</button></span>`
          : badge(text, 'warn'),
      );
    }
    if (s.frameOffset) out.push(badge(t('badge.offset', { id, n: fmtSigned(s.frameOffset) }), 'info'));
  }
  const d = app.view.lastDisplay;
  if (d?.proxy.has(id)) out.push(badge(t(app.player.playing ? 'badge.proxy' : 'badge.proxyPaused'), 'warn'));
  if (d?.pending.has(id)) out.push(badge(t('badge.decoding'), 'info'));
  const c = app.sources.client(id);
  const f = at?.frame;
  if (c && f !== null && f !== undefined && c.frameErrors.has(f)) out.push(badge(t('badge.frameError', { id, n: f }), 'error'));
}

function viewStateBadges(app: App, out: string[]): void {
  const p = app.project;
  const L = p.layout;
  if (p.viewAdjustment.enabled) out.push(badge(t('badge.adjust') + (p.viewAdjustment.linked ? '' : ' · ' + t('badge.adjustB', { id: L.selectedB })), 'warn'));
  if (L.channel !== 'rgb') out.push(badge(t('badge.channel', { c: t('channel.' + L.channel) }), 'warn'));
  if (L.textureView) out.push(badge(t('badge.texture'), 'warn'));
  if (app.player.playing && app.player.dropped > 0 && app.state.settings.degradeMode === 'quality')
    out.push(badge(t('badge.dropping') + ` (${app.player.dropped})`, 'warn'));
}

function dropzone(app: App, id: SourceId): string {
  const missing = app.project.sources.find((s) => s.id === id && !app.sources.runtime.has(id));
  const err = app.sources.errors.get(id);
  const loading = app.sources.loading.has(id);
  let title = t(id === 'A' ? 'drop.A' : 'drop.B', { id });
  let sub = t('drop.hint');
  if (missing) {
    title = t('drop.relink', { id, name: missing.file.name });
    sub = t('drop.relinkHint');
  }
  let extra = '';
  const tc = app.transcoding.get(id);
  if (loading) extra = `<div class="dz-progress">${esc(t('drop.loading'))}</div>`;
  else if (missing?.remote && app.ws.fetching.has(missing.remote.id)) extra = `<div class="dz-progress">${esc(t('drop.refetching'))}</div>`;
  if (tc !== undefined) extra = `<div class="dz-progress">${esc(t('drop.transcoding', { p: Math.round(tc * 100) }))}</div>`;
  else if (err)
    extra = `<div class="dz-error">${esc(t('drop.error', { name: err.fileName }))}\n${esc(t(err.errorKey, { codec: err.spec?.codec ?? err.detail }))}\n${esc(t('drop.transcodeHint'))}</div>`;
  return (
    `<div class="dropzone"><div class="dz-title">${esc(title)}</div>` +
    `<div class="dz-sub">${esc(sub)}</div>${extra}` +
    `<div class="dz-actions"><button data-pick="${id}">${esc(t('drop.pick'))}</button><button data-pickdir="${id}" title="${esc(t('drop.pickSeqTip'))}">${esc(t('drop.pickSeq'))}</button>` +
    `<button data-link="${id}" title="${esc(t('drop.linkTip'))}">${esc(t('drop.link'))}</button></div></div>`
  );
}

export function paneOverlayHtml(app: App, p: Pane): string {
  if (p.kind === 'empty') return dropzone(app, p.src);
  const out: string[] = [];
  const L = app.project.layout;
  const mode = app.view.compareMode();
  if (p.kind === 'composite') {
    const b = p.src;
    const a = app.view.compareBaseFor(b);
    const title =
      mode === 'wipe'
        ? t('composite.wipe', { a, b })
        : mode === 'overlay'
          ? t('composite.overlay', { a, b, p: Math.round(L.overlayOpacity * 100) })
          : mode === 'flicker'
            ? t('composite.flicker', { a, b, hz: app.state.settings.flickerHz })
            : t('composite.diff', { a, b, g: L.difference.gain }) + (L.difference.band && L.difference.band !== 'all' ? ' · ' + t('params.band.' + L.difference.band) : '');
    out.push(badge(title, 'label'));
    if (mode === 'flicker') out.push(badge(t('badge.flickerNow', { id: app.state.ui.flickerShowB ? b : a }), 'strong'));
    if (app.state.ui.alignOpen && mode === 'difference' && L.compareMode !== 'difference') out.push(badge(t('badge.alignDiff'), 'info'));
    sourceBadges(app, a, out, true);
    sourceBadges(app, b, out, true);
  } else {
    out.push(badge(sourceLabel(app, p.src), 'label'));
    if (p.kind === 'zoom' && L.zoomDifference && p.src !== 'A') out.push(badge(t('badge.zoomDiff', { id: p.src }), 'warn'));
    sourceBadges(app, p.src, out, p.kind !== 'zoom');
  }
  if (p.kind === 'zoom') out.push(badge(t('badge.zoom', { p: L.crosshair.zoomPercent }), 'zoom'));
  else {
    const s = p.view.s * app.project.compareScale;
    const is100 = Math.abs(p.view.s - 1) < 1e-6;
    out.push(badge(t('badge.display', { p: Math.round(s * 100) }) + (app.view.dpr !== 1 ? ` · DPR ${app.view.dpr}` : ''), is100 ? '' : 'warn'));
  }
  const firstZoom = app.view.panes.findIndex((q) => q.kind === 'zoom');
  if (p.kind !== 'zoom' || app.view.panes.indexOf(p) === firstZoom) viewStateBadges(app, out);
  // Per-source menu (F-04.8) at the top right of each picture pane (the zoom panes repeat the same sources).
  const more = p.kind === 'zoom' ? '' : `<button class="pane-more" data-more="${p.src}" title="${esc(t('srcmenu.tip', { id: p.src }))}">⋯</button>`;
  return `<div class="badges">${out.join('')}</div>${more}`;
}

let lastHtml = '';
export function updateOverlays(app: App): void {
  const host = $('#overlays');
  const canvas = app.view.canvas;
  const k = canvas.width ? canvas.getBoundingClientRect().width / canvas.width : 1;
  const html = app.view.panes
    .map((p, i) => {
      const r = p.rect;
      return `<div class="pane-ov ${p.kind}" data-i="${i}" data-src="${p.src}" style="left:${r.x * k}px;top:${r.y * k}px;width:${r.w * k}px;height:${r.h * k}px">${paneOverlayHtml(app, p)}</div>`;
    })
    .join('');
  if (html !== lastHtml) {
    host.innerHTML = html;
    lastHtml = html;
  }
}
